import { IBookingQuoteRevisionLine } from './IBooking';

/**
 * Where a quote request is in its life. The flow, and the only one there is:
 *
 *   Sent → Quoted → Accepted → Converted
 *     │       │
 *     │       └→ Declined
 *     └→ Expired (nadie contestó) / Cancelled (el cliente se echó atrás)
 */
export type ServiceRequestStatus =
  | 'Sent'
  | 'Quoted'
  | 'Accepted'
  | 'Declined'
  | 'Expired'
  | 'Cancelled'
  | 'Converted';

/** How soon they need it. Not a date and not a promise -- it is what lets a professional triage ten of these on a phone. */
export type ServiceRequestUrgency = 'Flexible' | 'ThisWeek' | 'Urgent';

export const SERVICE_REQUEST_URGENCIES: ServiceRequestUrgency[] = ['Flexible', 'ThisWeek', 'Urgent'];

/** One version of a quote on a request -- same meaning as a booking's own revision. */
export interface IServiceRequestQuote {
  versionNumber: number;
  amount: number;
  /** Null on the first version: there was nothing to explain yet. */
  reason?: string;
  proposedByUserId: string;
  createdAt: string;
  /** The window offered alongside this price, when one was offered. */
  proposedFrom?: string;
  proposedTo?: string;
  lines: IBookingQuoteRevisionLine[];
}

/**
 * A quote request, as either side sees it.
 *
 * Note what is NOT here and never will be: a start, an end or a duration
 * chosen by the customer. The dates only appear when the professional fixes
 * the visit, and by then this is a booking.
 */
export interface IServiceRequest {
  id: string;
  status: ServiceRequestStatus;
  urgency: ServiceRequestUrgency;
  description: string;

  /** Null whenever they did not use the assistant, which is a perfectly ordinary way to send a request. */
  assistantSummary?: string;
  /** What the assistant asked and what they tapped. Empty when they did not use it. */
  clarifications: IServiceRequestClarification[];
  /** What they photographed. Ids only -- each one is fetched through the authenticated endpoint. */
  photos: IServiceRequestPhoto[];

  serviceProviderProfileId: string;
  providerDisplayName: string;

  /** Empty when they asked the professional rather than one of their listings. */
  serviceId: string;
  serviceTitle: string;

  customerUserId: string;
  customerDisplayName: string;

  city?: string;
  province?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;

  /** A hint, never a reservation. */
  preferredFrom?: string;
  preferredTo?: string;

  currencyCode: string;

  /**
   * What this professional's visit costs, in currencyCode -- 0 when they do not
   * charge for it, which is the common case.
   *
   * Snapshotted when the request was sent (see ServiceRequest.VisitFeeAmount on
   * the backend), so it is the figure the customer was shown, not whatever the
   * professional has on their profile today. It is also the first line of every
   * version of the quote, written by the server rather than by either of them.
   */
  visitFeeAmount?: number;

  respondByUtc?: string;
  /** Seconds left for the professional to answer, computed server-side so a wrong device clock can't count down to the wrong moment. */
  respondInSeconds?: number;

  /**
   * The professional's deadline to fix the visit, once the customer has
   * accepted. Undefined in every other state -- the two clocks never run at
   * the same time, and both of them are the professional's.
   */
  confirmByUtc?: string;
  /** Seconds left to fix the visit, server-side for the same reason as respondInSeconds. */
  confirmInSeconds?: number;

  closedReason?: string;
  conversationId: string;
  /** The booking this became. Empty until the visit is fixed. */
  convertedBookingId: string;

  /** The price on the table right now. Undefined while nobody has quoted. */
  currentAmount?: number;
  currentVersion?: number;
  proposedFrom?: string;
  proposedTo?: string;

  createdAt: string;
  quoteHistory: IServiceRequestQuote[];
}

/** A row in either side's list -- the request without its whole negotiation. */
export interface IServiceRequestSummary {
  id: string;
  status: ServiceRequestStatus;
  urgency: ServiceRequestUrgency;
  descriptionPreview: string;
  serviceTitle: string;
  peerDisplayName: string;
  city?: string;
  currencyCode: string;
  currentAmount?: number;
  respondByUtc?: string;
  respondInSeconds?: number;
  confirmByUtc?: string;
  confirmInSeconds?: number;
  createdAt: string;
  conversationId: string;
}

/**
 * One photo of the job. No URL on purpose: the bytes come back from an
 * endpoint that checks who is asking -- these are pictures of the inside of
 * somebody's house.
 */
export interface IServiceRequestPhoto {
  id: string;
  uploadedAt: string;
}

/** One question the assistant asked, and what the customer answered. */
export interface IServiceRequestClarification {
  question: string;
  answer: string;
}

/** One thing worth knowing, with what the customer can tap to answer it. */
export interface IClarifyingQuestion {
  question: string;
  /** Two to four, in the customer's own words. The screen adds its own "no lo sé". */
  options: string[];
}

export interface ICreateServiceRequestRequest {
  /**
   * Who to ask. Only needed when no `serviceId` is sent -- with one, the
   * server derives it from the listing, so the two can never disagree.
   */
  serviceProviderProfileId?: string;
  serviceId?: string;
  description: string;
  urgency?: ServiceRequestUrgency;
  preferredFrom?: string;
  preferredTo?: string;
  city?: string;
  province?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  /** Kept beside the description, never merged into it -- the professional has to read the customer's own words. */
  clarifications?: IServiceRequestClarification[];
  assistantSummary?: string;
  /** Already uploaded and not yet attached to anything. Only the caller's own are taken. */
  photoIds?: number[];
}

/** One line the professional is quoting. Same shape the deferred-booking editor already speaks. */
export interface IServiceQuoteLine {
  title: string;
  description?: string;
  amount: number;
  origin: string;
  quantity?: number;
  unitPrice?: number;
  unit?: string;
  priceSource?: string;
  catalogKey?: string;
}

export interface IQuoteServiceRequestRequest {
  lines: IServiceQuoteLine[];
  /** Required from the second version onwards -- it is what the customer reads next to the new number. */
  reason?: string;
  proposedFrom?: string;
  proposedTo?: string;
}

/** The professional fixing the visit. Omitting the dates keeps the window from the accepted quote. */
export interface IConfirmServiceVisitRequest {
  startAt?: string;
  endAt?: string;
  /** Required when the request never named a listing: a booking line has to name a service. */
  serviceId?: string;
}
